import { type ExchangeToken, exchangeToken } from "@app/auth";
import { selectEnv } from "@app/config";
import {
  selectAllFeatureFlags,
  selectOrganizationSelected,
} from "@app/organizations";
import { useDispatch, useLoader, useSelector } from "@app/react";
import { homeUrl } from "@app/routes";
import { selectAccessToken, selectIsImpersonated } from "@app/token";
import { selectCanImpersonate, selectCurrentUser } from "@app/users";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AppSidebarLayout } from "../layouts";
import {
  BannerMessages,
  Button,
  CopyTextButton,
  FormGroup,
  Group,
  IconInfo,
  Input,
  Pill,
  Switch,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tooltip,
  Tr,
} from "../shared";
import { tokens } from "../shared/tokens";

function prepSubject(
  email = "",
  org = "",
  sso = false,
): Pick<
  ExchangeToken,
  "subjectToken" | "subjectTokenType" | "ssoOrganization"
> {
  const orgHref = org.match(/^http/)
    ? org
    : `https://auth.aptible.com/organizations/${org}`;

  if (sso) {
    return {
      subjectToken: email,
      subjectTokenType: "aptible:user:email",
      ssoOrganization: orgHref,
    };
  }

  if (email !== "") {
    return {
      subjectToken: email,
      subjectTokenType: "aptible:user:email",
    };
  }

  if (org !== "") {
    return {
      subjectToken: orgHref,
      subjectTokenType: "aptible:organization:href",
    };
  }

  return {
    subjectToken: "",
    subjectTokenType: "",
  };
}

export const ImpersonatePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const curOrg = useSelector(selectOrganizationSelected);
  const flags = useSelector(selectAllFeatureFlags);
  const config = useSelector(selectEnv);
  const isAptibleOrg = curOrg.id === config.aptibleOrgId;
  const loader = useLoader(exchangeToken);
  const canImpersonate = useSelector(selectCanImpersonate);
  const isImpersonated = useSelector(selectIsImpersonated);
  const actorToken = useSelector(selectAccessToken);
  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [writePerm, setWritePerm] = useState(false);
  const [sso, setSSO] = useState(false);
  const hasBothInputs = email !== "" && org !== "";
  const missingInputs = email === "" && org === "";
  const disableSubmit =
    loader.isLoading ||
    missingInputs ||
    (sso && !hasBothInputs) ||
    isImpersonated;

  useEffect(() => {
    if (user.id === "") return;
    // if the current token is being impersonated by a user then
    // we let them stay on this screen
    if (canImpersonate || isImpersonated) {
      return;
    }
    navigate(homeUrl());
  }, [canImpersonate, isImpersonated, user.id]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disableSubmit) {
      return;
    }

    dispatch(
      exchangeToken({
        actorToken,
        scope: writePerm ? "manage" : "read",
        ...prepSubject(email, org, sso),
      }),
    );
  };

  return (
    <AppSidebarLayout>
      <h2 className={tokens.type.h2}>Impersonation Access</h2>

      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <BannerMessages {...loader} />

        <FormGroup label="User Email" htmlFor="email">
          <Input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            className="w-full"
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
        </FormGroup>

        <FormGroup label="Organization ID/HREF" htmlFor="org">
          <Input
            type="text"
            id="org"
            name="org"
            className="w-full"
            onChange={(e) => setOrg(e.currentTarget.value)}
          />
        </FormGroup>

        {user.superuser ? (
          <Switch
            checked={writePerm}
            onChange={setWritePerm}
            ariaLabel="Write Access"
          >
            Write Access
          </Switch>
        ) : null}

        <div className="flex flex-col gap-1">
          <Switch
            checked={sso}
            onChange={setSSO}
            ariaLabel="Simulate SSO login for user in organization"
          >
            Simulate SSO login for user in organization
          </Switch>
          <p className={tokens.type["small lighter"]}>
            To simulate an SSO login, check here and enter both fields. This is
            always read only.
          </p>
        </div>

        <Button type="submit" disabled={disableSubmit}>
          Impersonate
        </Button>

        <div className="flex gap-2 items-center">
          <div className="font-bold w-1/5">Current User</div>
          <div className="flex-1">{user.email}</div>
          <div className="text-black-500 flex items-center gap-1">
            {user.id}
            <CopyTextButton text={user.id} />
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <div className="font-bold w-1/5">Current Organization</div>
          <div className="flex-1">{curOrg.name}</div>
          <div className="text-black-500 flex items-center gap-1">
            {curOrg.id}
            <CopyTextButton text={curOrg.id} />
          </div>
        </div>
      </form>

      <hr className="my-6" />

      <Group>
        <Group size="sm">
          <h3 className={tokens.type.h3}>Feature Flags</h3>
          <p className={tokens.type["small lighter"]}>
            See which features this account has access to.
          </p>
        </Group>

        <Table>
          <THead>
            <Th>Flag</Th>
            {isAptibleOrg ? (
              <Th variant="right">
                <span className="inline-flex items-center gap-1">
                  Total Orgs
                  <Tooltip
                    fluid
                    text="Only visible within Aptible account"
                    placement="bottom"
                  >
                    <IconInfo
                      className="opacity-50 hover:opacity-100"
                      variant="sm"
                    />
                  </Tooltip>
                </span>
              </Th>
            ) : null}
            <Th variant="right">Status</Th>
          </THead>
          <TBody>
            {flags.map((flag) => (
              <Tr key={flag.key}>
                <Td>{flag.label}</Td>
                {isAptibleOrg ? (
                  <Td>
                    <span className="block text-right pr-4">
                      {flag.orgIds.length}
                    </span>
                  </Td>
                ) : null}
                <Td variant="right">
                  <Pill variant={flag.enabled ? "success" : "default"}>
                    {flag.enabled ? "Enabled" : "Disabled"}
                  </Pill>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </Group>
    </AppSidebarLayout>
  );
};
